import { Link } from "react-router-dom";
import temas from "../temas/registry.js";
import { useFeed, vigentes } from "./feed";
import {
  BloquePodcast, Confianza, MetaNoticia, NoticiaColumna, TarjetaExpediente,
  pistaDe, recorta, rutaNoticia, etiquetaCorta, MarcaIdioma, Traducida,
} from "./piezas";
import { BotonPlay } from "./Reproductor";
import { IDIOMA, de, tr, seccion } from "../i18n/idioma";
import Video from "../components/Video";
import { TarjetaGuia, guiasOrdenadas } from "../guias/Guias";

/* Portada (handoff 1a, broadsheet). La portada cambia en cada ciclo (06:00 y
   18:00): la noticia principal sale SIEMPRE de la última edición (las piezas con
   el `publicado` más reciente, que es la hora del ciclo), la de vídeo si la hay
   y si no la de mayor relevancia. El resto se ordena por relevancia rebajada un
   30 % por cada ciclo de antigüedad, así una pieza fuerte baja hacia la lista y
   sale de portada antes de caducar (sigue en /noticias). Sin noticias todavía, el
   expediente más reciente ocupa la apertura para que la portada nunca quede vacía.
   El vídeo va panorámico en el sitio de la imagen (parado: la miniatura sale de
   yamacoa.com y YouTube no se carga hasta el clic). */

// Primer vídeo vigente de la noticia y su nota en el idioma de la página.
function primerVideo(n) {
  const k = (n.videos || []).findIndex((v) => !v.retirado && /youtu/.test(v.url || ""));
  if (k < 0) return null;
  return { v: n.videos[k], nota: (IDIOMA === "en" && n.en?.videos?.[k]?.nota) || n.videos[k].nota };
}

const CICLO = 12 * 3600000;
const REBAJA_POR_CICLO = 0.7;

// Ciclos de antigüedad respecto a la última edición (no al reloj: si un ciclo
// falla, la portada no se vacía ni cambia sola).
function ciclos(n, ultima) {
  return Math.max(0, Math.round((ultima - Date.parse(n.publicado || 0)) / CICLO));
}

function ordenarPortada(items) {
  const ultima = Math.max(0, ...items.map((n) => Date.parse(n.publicado || 0) || 0));
  const peso = (n) => (n.relevancia || 0) * REBAJA_POR_CICLO ** ciclos(n, ultima);
  const edicion = items.filter((n) => ciclos(n, ultima) === 0)
    .sort((a, b) => (b.relevancia || 0) - (a.relevancia || 0));
  const principal = edicion.find(primerVideo) || edicion[0];
  const resto = items.filter((n) => n !== principal).sort((a, b) => peso(b) - peso(a));
  return { principal, resto };
}

export const expedientesOrdenados = [...temas].sort((a, b) =>
  String(b.publicado || "").localeCompare(String(a.publicado || ""))
);

const CSS = `
.yp-grid{display:grid; grid-template-columns:2fr 1fr; gap:32px; padding-top:28px;}
.yp-princ h1{margin:12px 0 14px;}
.yp-img{margin-top:24px; border-radius:24px; overflow:hidden; background:var(--y-surface-2);}
.yp-img img{display:block; width:100%; height:clamp(220px,32vw,320px); object-fit:cover;}
.yp-img figcaption{font:500 10px var(--y-mono); letter-spacing:.06em; color:var(--y-subtle); padding:8px 12px;}
.yp-tres{display:grid; grid-template-columns:repeat(3,1fr); gap:24px; margin-top:26px; padding-top:22px;
  border-top:1px solid var(--y-rule);}
.yp-lado{display:flex; flex-direction:column; gap:20px;}
.yp-lista{list-style:none; margin:0; padding:0;}
.yp-lista li{display:flex; justify-content:space-between; gap:12px; padding:14px 0; border-bottom:1px solid var(--y-rule);
  font-size:15px; font-weight:600; line-height:1.3;}
.yp-exp{margin-top:48px; padding-top:22px;}
.yp-exp-h{display:flex; justify-content:space-between; align-items:baseline; margin-bottom:20px; gap:12px;}
.yp-tri{display:grid; grid-template-columns:repeat(3,1fr); gap:18px;}
@media (max-width:900px){
  .yp-grid, .yp-tri{grid-template-columns:1fr;}
  .yp-tres{grid-template-columns:1fr;}
}
`;

export default function Portada() {
  const noticias = useFeed("noticias.json");
  const podcast = useFeed("podcast.json");

  const { principal, resto } = ordenarPortada(vigentes(noticias.datos?.items));
  // La guía práctica más reciente ocupa el sitio que tenía el estudio automático del día.
  const guia = guiasOrdenadas[0];
  // El último episodio en el idioma de la página (el feed mezcla «Yamacoa al día» y «Yamacoa Today»).
  const episodio = podcast.datos?.items?.find((p) => (p.idioma || "es") === IDIOMA) || podcast.datos?.items?.[0];
  const columnas = resto.slice(0, 3);
  const mas = resto.slice(3, 9);

  return (
    <div className="y-wrap">
      <style>{CSS}</style>
      <div className="yp-grid">
        <div>
          {principal ? <Principal n={principal} /> : <AperturaExpediente t={expedientesOrdenados[0]} />}
          {columnas.length > 0 && (
            <div className="yp-tres">
              {columnas.map((n) => <NoticiaColumna key={n.id} n={n} />)}
            </div>
          )}

        </div>

        <aside className="yp-lado" aria-label={tr("Destacados", "Highlights")}>
          {guia && <TarjetaGuia g={guia} rotulo={tr("Guía práctica", "Practical guide")} />}
          <BloquePodcast p={episodio} />
          {mas.length > 0 && (
            <div>
              <p className="y-meta" style={{ textTransform: "uppercase", marginBottom: 4 }}>{tr("Más noticias de la semana", "More news this week")}</p>
              <ul className="yp-lista">
                {mas.map((n) => (
                  <li key={n.id}>
                    <Link className="y-link" to={rutaNoticia(n)}>{de(n, "titulo")}</Link>
                    <span className="y-meta">{seccion(n.seccion)} <MarcaIdioma n={n} /></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!principal && (
            <div className="y-card" style={{ background: "var(--y-surface-2)", boxShadow: "none" }}>
              <p className="y-eyebrow">{tr("Redacción automática", "Automated newsroom")}</p>
              <p className="y-card-p" style={{ marginTop: 8 }}>
                {tr("Las noticias automáticas verificadas aparecerán aquí y vivirán tres días en portada.",
                  "Verified automated news will appear here and stay on the front page for three days.")}
              </p>
            </div>
          )}
        </aside>
      </div>

      <section className="yp-exp y-rule-top" aria-labelledby="yp-exp-t">
        <div className="yp-exp-h">
          <h2 id="yp-exp-t" className="y-h2">{tr("Expedientes dirigidos", "Editorial investigations")}</h2>
          <Link to="/expedientes" className="y-link" style={{ fontWeight: 600, fontSize: 15 }}>{tr("Archivo completo →", "Full archive →")}</Link>
        </div>
        <div className="yp-tri">
          {expedientesOrdenados.slice(principal ? 0 : 1, principal ? 3 : 4).map((t) => (
            <TarjetaExpediente key={t.id} t={t} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Principal({ n }) {
  const vid = primerVideo(n);
  return (
    <article className="yp-princ">
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span className="y-badge y-badge--auto">{tr("Noticia automática", "Automated news")}</span>
        <MetaNoticia n={n} />
        <Confianza valor={n.confianza} />
        <MarcaIdioma n={n} />
      </div>
      <h1 className="y-h1"><Link className="y-link" to={rutaNoticia(n)}>{de(n, "titulo")}</Link></h1>
      <Traducida n={n} ruta={rutaNoticia(n)} />
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <p className="y-lede" style={{ maxWidth: 640, flex: 1 }}>{de(n, "entradilla")}</p>
        <BotonPlay pista={pistaDe(n, tr("Resumen hablado", "Audio summary"))} oscuro
          etiqueta={tr(`resumen de ${n.titulo}`, `summary of ${de(n, "titulo")}`)} />
      </div>
      {/* Con vídeo, el vídeo ocupa el sitio de la imagen (parado; se carga al pulsar). */}
      {vid ? <Video v={vid.v} nota={vid.nota} portada /> : n.imagen?.url && (
        <figure className="yp-img" style={{ margin: "24px 0 0" }}>
          <img src={n.imagen.url} alt={de(n.imagen, "alt") || ""} loading="eager" />
          {/* El tipo va siempre a la vista: una ilustración de IA se rotula como tal (§11.4). */}
          <figcaption>{{ foto: tr("Fotografía", "Photograph"), grafico: tr("Gráfico Yamacoa", "Yamacoa chart"),
            ilustracion_ia: tr("Ilustración generada por IA", "AI-generated illustration") }[n.imagen.tipo] || tr("Imagen", "Image")}
            {n.imagen.credito ? ` · ${n.imagen.credito}` : ""}</figcaption>
        </figure>
      )}
    </article>
  );
}

function AperturaExpediente({ t }) {
  if (!t) return null;
  return (
    <article className="yp-princ">
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span className="y-badge y-badge--dir">{tr("Expediente dirigido", "Editorial investigation")}</span>
        <span className="y-meta">{etiquetaCorta(de(t, "etiqueta"))} · {de(t, "fuentes")}</span>
      </div>
      <h1 className="y-h1"><Link className="y-link" to={t.path}>{de(t, "titulo")}</Link></h1>
      <p className="y-lede" style={{ maxWidth: 680 }}>{recorta(de(t, "subtitulo"), 260)}</p>
      <p style={{ marginTop: 22 }}>
        <Link to={t.path} className="y-btn">{tr("Abrir expediente →", "Open investigation →")}</Link>
      </p>
    </article>
  );
}
